/**
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc. All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * ViewCourseIdModal
 *
 * @file ViewCourseIdModal.jsx
 * @author Ujjwal Sana
 * @since 1.0.0
 */

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@mui/material';
import Icons from '../icons/Icon';
import {
  CourseIdDialogPaper,
  CourseIdCloseButton,
  CourseIdContentRoot,
  CourseIdSectionCard,
  CourseIdMainRow,
  CourseIdGroup,
  CourseIdLabel,
  CourseIdText,
  CourseIdCopyIconButton,
  CourseIdIconTextWrapper,
  CourseIdCopyText
} from './styles/viewCourseIdModalStyle';

const ViewCourseIdModal = ({ open, onClose, courseId, textInfo }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator?.clipboard?.writeText(courseId);
    setCopied(true);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={CourseIdDialogPaper}
    >
      {/* Close */}
      <CourseIdCloseButton
        aria-label="Close"
        onClick={onClose}
      >
        <Icons name="CloseIcon" />
      </CourseIdCloseButton>

      {/* Content */}
      <CourseIdContentRoot>
        <CourseIdSectionCard>
          <CourseIdMainRow>
            {/* Left side */}
            <CourseIdGroup>
              <CourseIdLabel>{textInfo.courseLabel}</CourseIdLabel>
              <CourseIdText title={courseId}>
                {courseId}
              </CourseIdText>
            </CourseIdGroup>

            {/* Right side – IconButton */}
            <CourseIdCopyIconButton
              onClick={handleCopy}
            >
              <CourseIdIconTextWrapper>
                {copied ? (
                  <Icons name="CopiedIcon" />
                ) : (
                  <Icons name="CopyIcon" />
                )}
                <CourseIdCopyText>
                  {copied ? textInfo.copied : textInfo.copy}
                </CourseIdCopyText>
              </CourseIdIconTextWrapper>
            </CourseIdCopyIconButton>
          </CourseIdMainRow>
        </CourseIdSectionCard>
      </CourseIdContentRoot>
    </Dialog>
  );
};

ViewCourseIdModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  courseId: PropTypes.string.isRequired,
  textInfo: PropTypes.shape({
    copy: PropTypes.string,
    copied: PropTypes.string,
    courseLabel: PropTypes.string
  })
};

ViewCourseIdModal.defaultProps = {
  open: false,
  onClose: () => {},
  textInfo: { copy: 'Copy', copied: 'Copied', courseLabel: 'Course ID:' }
};

export default ViewCourseIdModal;
